import { inject, Injectable } from '@angular/core';
import { AppNotificationSeverity } from './app-notification-severity';
import { AppNotificationGateway } from './app-notification.gateway';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { AppErrorItem } from '@domain-utils/app-error-item';

const TITLE_KEYS = {
  success: 'notification.successTitle',
  error: 'notification.errorTitle',
  warning: 'notification.warningTitle',
  info: 'notification.infoTitle'
} as const;

/**
 * Servicio de notificaciones
 */
@Injectable({
  providedIn: 'root'
})
export class AppNotificationService {
  /**
   * Servicio de traducción
   */
  private readonly translateService = inject(AppTranslationService);

  /**
   * Constructor
   * @param notificationGateway gateway de notificaciones
   */
  constructor(private readonly notificationGateway: AppNotificationGateway) {}

  /**
   * Método para mostrar una notificación de éxito
   * @param message  mensaje de la notificación
   */
  success(message: string): void {
    this.notifyWithTranslation(TITLE_KEYS.success, message, AppNotificationSeverity.SUCCESS);
  }

  /**
   * Método para mostrar una notificación de error
   * @param message mensaje de la notificación
   * @param translate si se deben traducir el mensaje
   */
  error(message: string, translate = false): void {
    const titleKey = TITLE_KEYS.error;
    const messagesToTranslate = translate
      ? [{ key: titleKey }, { key: message }]
      : [{ key: titleKey }];

    this.translateService
      .getMany(messagesToTranslate)
      .subscribe((translations) => {
        const title = translations.find((t) => t.key === titleKey)?.value ?? titleKey;
        const translatedMessage = translate
          ? translations.find((t) => t.key === message)?.value ?? message
          : message;

        this.notificationGateway.notify({
          title,
          message: translatedMessage,
          severity: AppNotificationSeverity.ERROR
        });
      });
  }

  /**
   * Método para mostrar una notificación de error con varios mensajes
   * @param messages mensajes de la notificación
   * @param translate si se deben traducir los mensajes, default: false
   */
  errorMany(messages: AppErrorItem[], translate = false): void {
    messages.forEach((msg) => {
      this.error(msg.message, translate);
    });
  }

  /**
   * Método para mostrar una notificación de advertencia
   * @param message mensaje de la notificación
   */
  warn(message: string): void {
    this.notifyWithTranslation(TITLE_KEYS.warning, message, AppNotificationSeverity.WARNING);
  }

  /**
   * Método para mostrar una notificación de información
   * @param message mensaje de la notificación
   */
  info(message: string): void {
    this.notifyWithTranslation(TITLE_KEYS.info, message, AppNotificationSeverity.INFO);
  }

  /**
   * Traduce título y mensaje, luego emite la notificación
   */
  private notifyWithTranslation(
    titleKey: string,
    messageKey: string,
    severity: AppNotificationSeverity
  ): void {
    this.translateService
      .getMany([{ key: titleKey }, { key: messageKey }])
      .subscribe((translations) => {
        const title = translations.find((t) => t.key === titleKey)?.value ?? titleKey;
        const message = translations.find((t) => t.key === messageKey)?.value ?? messageKey;

        this.notificationGateway.notify({ title, message, severity });
      });
  }
}
